import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography, Box, Paper, IconButton, Tooltip, Switch, Fab,
  CircularProgress, Modal, Button, Chip, Table, TableHead,
  TableBody, TableRow, TableCell, TableSortLabel,
} from "@mui/material";
import { useNotification } from '../context/NotificationContext';
import EditNoteIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useAuth } from '../context/AuthContext';
import { useBriefings, useToggleBriefing, useDeleteBriefing } from '../hooks/useBriefings';

const TEMPLATE_LABELS = {
  competitor_watch: 'Praćenje konkurencije',
  crisis_monitor: 'Krizno praćenje',
  industry_trends: 'Trendovi u industriji',
  brand_health: 'Zdravlje branda',
};

const formatDate = (isoString) => {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleString('hr-HR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return isoString;
  }
};

const sortRows = (rows, key, dir) =>
  [...rows].sort((a, b) => {
    let av, bv;
    switch (key) {
      case 'name': av = a.name ?? ''; bv = b.name ?? ''; break;
      case 'terms': av = (a.terms?.length ?? 0); bv = (b.terms?.length ?? 0); break;
      case 'template': av = a.template ?? ''; bv = b.template ?? ''; break;
      case 'last_run': av = a.timestamp_last_run ?? ''; bv = b.timestamp_last_run ?? ''; break;
      case 'is_active': av = a.is_active ? 1 : 0; bv = b.is_active ? 1 : 0; break;
      default: av = ''; bv = '';
    }
    if (typeof av === 'string') { av = av.toLowerCase(); bv = bv.toLowerCase(); }
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return dir === 'asc' ? cmp : -cmp;
  });

const toggleDir = (prev, key) => ({
  key,
  dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
});

const Briefings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();

  const briefingLimit = user?.briefing_limit ?? 0;
  const { data: briefings = [], isLoading } = useBriefings();
  const toggleBriefing = useToggleBriefing();
  const deleteBriefing = useDeleteBriefing();

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });

  useEffect(() => { document.title = 'Smart Brifinzi | Media Scanner'; }, []);

  const handleToggle = (b) => {
    toggleBriefing.mutate(
      { id: b.id, is_active: !b.is_active },
      {
        onSuccess: () => showSuccess(b.is_active ? 'Briefing deaktiviran' : 'Briefing aktiviran'),
        onError: () => showError('Greška pri promjeni statusa briefinga'),
      }
    );
  };

  const handleDelete = (id) => {
    deleteBriefing.mutate({ id, is_active: false }, {
      onSuccess: () => {
        setConfirmDeleteOpen(false);
        setDeleteTargetId(null);
        showSuccess('Briefing obrisan');
      },
      onError: () => showError('Greška pri brisanju briefinga'),
    });
  };

  const sortProps = (key) => ({
    active: sort.key === key,
    direction: sort.key === key ? sort.dir : 'asc',
    onClick: () => setSort(prev => toggleDir(prev, key)),
  });

  const sortedBriefings = sortRows(briefings, sort.key, sort.dir);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2, py: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50' }}>
            Smart Brifinzi
          </Typography>
          {!isLoading && (
            <Chip
              label={`${briefings.length} / ${briefingLimit}`}
              size="small"
              sx={{ fontWeight: 600, backgroundColor: '#f3e5f5', color: '#7b1fa2' }}
            />
          )}
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : briefings.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <AutoAwesomeIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              Nemate postavljenih Smart Brifinga.
            </Typography>
            {briefings.length < briefingLimit && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                sx={{ mt: 2 }}
                onClick={() => navigate("/briefingCreate")}
              >
                Kreiraj prvi briefing
              </Button>
            )}
          </Paper>
        ) : (
          <Paper sx={{ overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <TableSortLabel {...sortProps('name')}>Naziv</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <TableSortLabel {...sortProps('terms')}>Pojmovi</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <TableSortLabel {...sortProps('template')}>Predložak</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <TableSortLabel {...sortProps('last_run')}>Zadnje pokretanje</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <TableSortLabel {...sortProps('is_active')}>Status</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 100 }} align="right">Akcije</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedBriefings.map((b) => (
                  <TableRow key={b.id} hover>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: '#7b1fa2', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        onClick={() => navigate(`/briefing-details/${b.id}`)}
                      >
                        {b.name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, color: '#555', maxWidth: 250 }}>
                      {Array.isArray(b.terms) && b.terms.length
                        ? b.terms.map(t => t.name ?? '').filter(Boolean).join(', ')
                        : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {b.template ? (
                        <Chip label={TEMPLATE_LABELS[b.template] || b.template} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                      ) : '—'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                      {formatDate(b.timestamp_last_run)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            px: 1, py: 0.25, borderRadius: 1, fontWeight: 600,
                            backgroundColor: b.is_active ? '#e8f5e9' : '#f5f5f5',
                            color: b.is_active ? '#27ae60' : '#999',
                          }}
                        >
                          {b.is_active ? 'Aktivan' : 'Neaktivan'}
                        </Typography>
                        <Tooltip title={b.is_active ? "Deaktiviraj" : "Aktiviraj"}>
                          <Switch size="small" checked={b.is_active} onChange={() => handleToggle(b)} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Uredi">
                        <IconButton size="small" onClick={() => navigate(`/briefing-details/${b.id}`)}>
                          <EditNoteIcon sx={{ color: '#7b1fa2', fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Obriši">
                        <IconButton size="small" onClick={() => { setDeleteTargetId(b.id); setConfirmDeleteOpen(true); }}>
                          <DeleteIcon sx={{ color: '#e74c3c', fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Box>

      {briefings.length < briefingLimit && (
        <Fab
          aria-label="Dodaj briefing"
          sx={{ position: 'fixed', bottom: 32, right: 32, boxShadow: 3, zIndex: 1000, backgroundColor: '#7b1fa2', color: '#fff', '&:hover': { backgroundColor: '#6a1b9a' } }}
          onClick={() => navigate("/briefingCreate")}
        >
          <AddIcon />
        </Fab>
      )}

      <Modal open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <Paper elevation={8} sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 420, borderRadius: 2, p: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>
            Brisanje briefinga
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Jeste li sigurni da želite izbrisati ovaj Smart Brifing? Ova akcija se ne može poništiti.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button variant="outlined" onClick={() => { setConfirmDeleteOpen(false); setDeleteTargetId(null); }}>Odustani</Button>
            <Button variant="contained" color="error" onClick={() => { if (deleteTargetId !== null) handleDelete(deleteTargetId); }}>Izbriši</Button>
          </Box>
        </Paper>
      </Modal>
    </Box>
  );
};

export default Briefings;
